import { useState, useEffect, useCallback, useRef } from 'react'
import { adminService } from '../../services/adminService'
import {
  LayoutDashboard,
  Users,
  Building2,
  Activity,
  Landmark,
  Heart,
  RefreshCw,
  AlertTriangle,
  Loader2,
  TrendingUp,
  UserPlus,
  Calculator,
} from 'lucide-react'

// ─── Types ─────────────────────────────────────────────────────────────────────

interface RoleBreakdown {
  business_owner: number
  lender: number
  admin: number
  analyst: number
  viewer: number
  system: number
}

interface DashboardMetrics {
  totalUsers: number
  usersByRole: RoleBreakdown
  totalBusinesses: number
  totalScoresCalculated: number
  activeLenders: number
  systemHealth: 'healthy' | 'degraded' | 'unhealthy'
}

interface TrendDay {
  date: string
  userRegistrations: number
  scoreCalculations: number
  lenderActivity: number
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const AUTO_REFRESH_INTERVAL = 65_000 // 65 seconds

const HEALTH_STATUS_CONFIG: Record<
  string,
  { label: string; color: string; bgColor: string; dotColor: string }
> = {
  healthy: {
    label: 'Healthy',
    color: 'text-green-700 dark:text-green-300',
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    dotColor: 'bg-green-500',
  },
  degraded: {
    label: 'Degraded',
    color: 'text-yellow-700 dark:text-yellow-300',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
    dotColor: 'bg-yellow-500',
  },
  unhealthy: {
    label: 'Unhealthy',
    color: 'text-red-700 dark:text-red-300',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    dotColor: 'bg-red-500',
  },
}

const ROLE_LABELS: Record<string, string> = {
  business_owner: 'Business Owners',
  lender: 'Lenders',
  admin: 'Admins',
  analyst: 'Analysts',
  viewer: 'Viewers',
  system: 'System',
}

// ─── Component ─────────────────────────────────────────────────────────────────

export function AdminDashboard() {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null)
  const [trends, setTrends] = useState<TrendDay[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // ─── Data Fetching ─────────────────────────────────────────────────────────

  const fetchData = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true)
    try {
      const [metricsRes, trendsRes] = await Promise.all([
        adminService.getDashboardMetrics(),
        adminService.getDashboardTrends(),
      ])
      setMetrics(metricsRes.data as DashboardMetrics)
      setTrends((trendsRes.data as { trends: TrendDay[] })?.trends || trendsRes.data as TrendDay[] || [])
      setLastRefreshed(new Date())
      setError(null)
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard metrics')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  // Initial load
  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Auto-refresh at 65-second interval
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      fetchData()
    }, AUTO_REFRESH_INTERVAL)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [fetchData])

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const formatNumber = (num: number): string => {
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`
    if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`
    return num.toLocaleString()
  }

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  const getMaxTrendValue = (key: keyof Omit<TrendDay, 'date'>): number => {
    if (trends.length === 0) return 1
    const max = Math.max(...trends.map((t) => t[key] as number))
    return max || 1
  }

  const formatTrendDate = (dateStr: string): string => {
    try {
      const date = new Date(dateStr)
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    } catch {
      return dateStr
    }
  }

  // ─── Loading State ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-400" />
        <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
          Loading dashboard...
        </span>
      </div>
    )
  }

  // ─── Error State ───────────────────────────────────────────────────────────

  if (error && !metrics) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-4">
        <AlertTriangle className="h-12 w-12 text-red-400 dark:text-red-500 mb-4" />
        <p className="text-gray-700 dark:text-gray-300 text-sm mb-4">{error}</p>
        <button
          onClick={() => {
            setLoading(true)
            fetchData()
          }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Retry
        </button>
      </div>
    )
  }

  const healthConfig = HEALTH_STATUS_CONFIG[metrics?.systemHealth || 'healthy']

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <LayoutDashboard className="h-7 w-7 text-blue-600 dark:text-blue-400" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Admin Dashboard
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Platform-wide metrics and usage trends
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {lastRefreshed && (
            <span className="text-xs text-gray-400 dark:text-gray-500">
              Last updated: {formatTime(lastRefreshed)}
            </span>
          )}
          <button
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error banner (non-blocking) */}
      {error && metrics && (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
          <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
          <p className="text-sm text-yellow-700 dark:text-yellow-300">
            Auto-refresh failed: {error}
          </p>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {/* Total Users */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
              <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Total Users
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {formatNumber(metrics?.totalUsers || 0)}
          </p>
          {/* Role breakdown */}
          {metrics?.usersByRole && (
            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-1">
              {Object.entries(metrics.usersByRole)
                .filter(([, count]) => count > 0)
                .sort(([, a], [, b]) => b - a)
                .map(([role, count]) => (
                  <div key={role} className="flex items-center justify-between text-xs">
                    <span className="text-gray-500 dark:text-gray-400">
                      {ROLE_LABELS[role] || role}
                    </span>
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      {count}
                    </span>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Total Businesses */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20">
              <Building2 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Total Businesses
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {formatNumber(metrics?.totalBusinesses || 0)}
          </p>
        </div>

        {/* Total Scores Calculated */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
              <Activity className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Scores Calculated
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {formatNumber(metrics?.totalScoresCalculated || 0)}
          </p>
        </div>

        {/* Active Lenders */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20">
              <Landmark className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Active Lenders
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {formatNumber(metrics?.activeLenders || 0)}
          </p>
        </div>

        {/* System Health */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-pink-50 dark:bg-pink-900/20">
              <Heart className="h-5 w-5 text-pink-600 dark:text-pink-400" />
            </div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              System Health
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`inline-block h-2.5 w-2.5 rounded-full ${healthConfig.dotColor}`} />
            <span className={`text-lg font-semibold ${healthConfig.color}`}>
              {healthConfig.label}
            </span>
          </div>
        </div>
      </div>

      {/* Trends Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            30-Day Trends
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* User Registrations Trend */}
          <TrendCard
            title="User Registrations"
            icon={<UserPlus className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
            trends={trends}
            dataKey="userRegistrations"
            barColor="bg-blue-500 dark:bg-blue-400"
            maxValue={getMaxTrendValue('userRegistrations')}
            formatDate={formatTrendDate}
          />

          {/* Score Calculations Trend */}
          <TrendCard
            title="Score Calculations"
            icon={<Calculator className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />}
            trends={trends}
            dataKey="scoreCalculations"
            barColor="bg-emerald-500 dark:bg-emerald-400"
            maxValue={getMaxTrendValue('scoreCalculations')}
            formatDate={formatTrendDate}
          />

          {/* Lender Activity Trend */}
          <TrendCard
            title="Lender Activity"
            icon={<Landmark className="h-4 w-4 text-amber-600 dark:text-amber-400" />}
            trends={trends}
            dataKey="lenderActivity"
            barColor="bg-amber-500 dark:bg-amber-400"
            maxValue={getMaxTrendValue('lenderActivity')}
            formatDate={formatTrendDate}
          />
        </div>
      </div>
    </div>
  )
}

// ─── TrendCard Sub-component ─────────────────────────────────────────────────

interface TrendCardProps {
  title: string
  icon: React.ReactNode
  trends: TrendDay[]
  dataKey: keyof Omit<TrendDay, 'date'>
  barColor: string
  maxValue: number
  formatDate: (dateStr: string) => string
}

function TrendCard({ title, icon, trends, dataKey, barColor, maxValue, formatDate }: TrendCardProps) {
  const total = trends.reduce((sum, t) => sum + (t[dataKey] as number), 0)

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {title}
          </h3>
        </div>
        <span className="text-xs text-gray-400 dark:text-gray-500">
          Total: {total.toLocaleString()}
        </span>
      </div>

      {trends.length === 0 ? (
        <div className="flex items-center justify-center py-8">
          <p className="text-sm text-gray-400 dark:text-gray-500">No data available</p>
        </div>
      ) : (
        <div className="space-y-0.5">
          {/* Sparkline-style vertical bars */}
          <div className="flex items-end gap-px h-24">
            {trends.map((day, idx) => {
              const value = day[dataKey] as number
              const heightPercent = maxValue > 0 ? (value / maxValue) * 100 : 0
              return (
                <div
                  key={idx}
                  className="flex-1 flex flex-col justify-end group relative"
                >
                  <div
                    className={`w-full rounded-t-sm ${barColor} transition-all duration-200 group-hover:opacity-80`}
                    style={{ height: `${Math.max(heightPercent, 2)}%` }}
                  />
                  {/* Tooltip on hover */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                    <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded px-2 py-1 whitespace-nowrap shadow-lg">
                      <div className="font-medium">{formatDate(day.date)}</div>
                      <div>{value.toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Date range labels */}
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {trends.length > 0 ? formatDate(trends[0].date) : ''}
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {trends.length > 0 ? formatDate(trends[trends.length - 1].date) : ''}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
